<template>
  <div id="sales_activity" class="flex flex-col gap-2">
    <ContainerInfoItem
      :source="`sales_activity_by_user_${type}`"
      :title="`Sales activity by ${renderEntity(filters?.user) || 'user'}`"
      :description="range.label"
      :showExportOption="type === analyticTypes.TABLE"
      :threeDotsDropdownItems="threeDotsDropdownItems"
      height="fit-content"
    >
      <SkeletonLoader
        v-if="isLoading"
        :page="
          type === analyticTypes.TABLE ? loaderPages.TABLE : loaderPages.CHART
        "
      />

      <template v-else>
        <CustomTable
          v-if="type === analyticTypes.TABLE && allTableData?.length > 0"
          :headers="salesActivityTableHeaders"
          :data="paginatedTableData"
          :show-page-size="false"
          :pagination="pagination"
          @update:pagination="handlePageChange"
          class="mt-4"
        />

        <BarChart
          v-else-if="
            type === analyticTypes.CHART && chartCategories && chartData
          "
          id="call_engagement_activity"
          :categories="chartCategories"
          :series-data="chartData"
        />

        <EmptyScreen
          v-else
          :source="`sales_activity_by_user_${type}`"
          text="No data found!"
          :type="emptyScreenTypes.CHART_AND_TABLE"
        />
      </template>
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import { message } from "ant-design-vue";
import { renderEntity } from "@/src/utils/utils.js";
import { downloadCSVFile } from "@/src/helpers/downloadHelper.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import {
  analyticTypes,
  salesActivityTableHeaders,
} from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import BarChart from "@/src/components/charts/BarChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) =>
      [analyticTypes.TABLE, analyticTypes.CHART].includes(value),
  },
});

const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const range = computed(() => store.getters["analytics/getRange"]);
const isLoading = ref(false);

const chartCategories = ref(null);
const chartData = ref(null);
const allTableData = ref([]);
const pagination = ref({
  current: 1,
  pageSize: 5,
  total: 0,
});

const threeDotsDropdownItems = computed(() => [
  {
    key: "export",
    action: () => handleExport(),
    icon: imageNames.EXPORT_GREY,
    text: "Export CSV",
  },
]);

const paginatedTableData = computed(() => {
  if (!allTableData.value) return [];

  const start = (pagination.value.current - 1) * pagination.value.pageSize;
  const end = start + pagination.value.pageSize;

  return allTableData.value.slice(start, end);
});

const ACTIVITY_TYPES = [
  { key: "calls_done", name: "Calls" },
  { key: "emails_sent", name: "Emails" },
  { key: "linkedin_connection_requests", name: "LinkedIn Connections" },
  { key: "linkedin_inmails_sent", name: "LinkedIn InMails" },
  { key: "linkedin_messages_sent", name: "LinkedIn Messages" },
];

const resetData = () => {
  chartCategories.value = null;
  chartData.value = null;
  allTableData.value = [];
  pagination.value = {
    current: 1,
    pageSize: 5,
    total: 0,
  };
};

const handlePageChange = (newPagination) => {
  pagination.value = newPagination;
};

const processChartData = (data) => {
  if (!data?.length) {
    resetData();
    return;
  }

  chartCategories.value = data.map((item) => item.user_name);
  chartData.value = ACTIVITY_TYPES.map((activity) => ({
    name: activity.name,
    data: data.map((user) => user[activity.key] || 0),
  }));

  if (
    !chartData.value.some((series) => series.data.some((value) => value !== 0))
  ) {
    resetData();
  }
};

const fetchData = async () => {
  try {
    isLoading.value = true;

    const params = {
      user_id: userIds.value,
      team_id: teamIds.value,
      start_time: range.value?.start,
      end_time: range.value?.end,
    };

    const response = await AnalyticService.V2.fetchSalesActivityByUser(params);

    if (response?.success && response?.data) {
      const { data, total_output } = response.data;

      if (props.type === analyticTypes.TABLE) {
        allTableData.value = data;
        pagination.value.total = total_output;
      } else {
        processChartData(data);
      }
    } else {
      resetData();
    }
  } catch (error) {
    console.error("Error fetching sales activities:", error);
    resetData();
  } finally {
    isLoading.value = false;
  }
};

const handleExport = async () => {
  try {
    const params = {
      user_id: userIds.value,
      team_id: teamIds.value,
      start_time: range.value?.start,
      end_time: range.value?.end,
    };

    const response = await AnalyticService.V2.exportSalesActivityByUser(params);

    downloadCSVFile(response, "sales_activity.csv");
    message.success("CSV exported successfully!");
  } catch (error) {
    message.error("Failed to download export file");
    console.error("Error downloading export file", error);
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) fetchData();
});

useAnalyticsRefresh(fetchData, [range, userIds, teamIds]);
</script>
